import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Select,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { HiOutlineDatabase } from 'react-icons/hi';

import { DatasetRef } from 'components/shared/Compare';
import useComparison from 'hooks/data/datasets/comparison/useComparison';
import useComparisonMethods from 'hooks/data/datasets/comparison/useComparisonMethods';
import { AccessResourceKey } from 'hooks/data/files/types';
import useAccess from 'hooks/useAccess';
import useSavedLciaMethod from 'hooks/useSavedLciaMethod';
import { checkAccessToResource } from 'utilities/auth/access';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { buildComparedIndicators } from './comparisonUtils';
import { CrossReleaseNotice, CrossVersionCaveat, describeReleases } from './CrossReleaseNotice';
import ImpactCategoryRow, { IMPACT_GRID_COLUMNS } from './ImpactCategoryRow';
import {
  CONTRIBUTOR_COLORS_DARK,
  CONTRIBUTOR_COLORS_LIGHT,
  hatchBackgroundImage,
  NO_BREAKDOWN_BG,
  NO_BREAKDOWN_HATCH,
} from './impactUtils';
import { useResolveCompareDataset } from './useResolveCompareDataset';

type Props = {
  refs: DatasetRef[];
};

const ImpactAssessmentComparison = ({ refs }: Props) => {
  const headerColor = useColorModeValue('gray.600', 'gray.400');
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const emptyBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const emptyIconColor = useColorModeValue('gray.300', 'whiteAlpha.400');
  const noBreakdownBg = useColorModeValue(NO_BREAKDOWN_BG.light, NO_BREAKDOWN_BG.dark);
  const noBreakdownHatch = useColorModeValue(NO_BREAKDOWN_HATCH.light, NO_BREAKDOWN_HATCH.dark);
  // Same ramp the rows use, so the legend can never drift out of step with the bars.
  const contributorColors = useColorModeValue(CONTRIBUTOR_COLORS_LIGHT, CONTRIBUTOR_COLORS_DARK);

  const [refA, refB] = [refs[0], refs[1]];
  const hasPair = Boolean(refA && refB);

  // The backend intersects the two versions' method lists, so the FE no longer fetches both.
  const {
    data: methods,
    isLoading: loadingMethods,
    isError: hasMethodsError,
  } = useComparisonMethods(refA?.version, refB?.version, refA?.systemModel);

  // Read-only seed: this page has two releases, so there is no correct release to persist against —
  // writing back would change the user's default on unrelated dataset pages. The endpoint already
  // returns the single-dataset method shape, so it seeds directly; it matches on name when its id
  // lookup misses, which is what we rely on across releases.
  const { selectedMethod: seedMethod } = useSavedLciaMethod(methods);
  const [pickedMethodName, setPickedMethodName] = useState<string>();

  // Method ids are release-scoped, so name is the only identity that survives across releases.
  const selectedMethod =
    methods?.find((method) => method.method_name === pickedMethodName) ??
    methods?.find((method) => method.method_name === seedMethod?.method_name) ??
    methods?.[0];

  // Guard on the id, not just the object: method_id is a required query param, and sending
  // `undefined` drops it from the URL entirely, which the API answers with a 422.
  const comparisonParams =
    refA && refB && selectedMethod?.method_id !== undefined
      ? {
          dataset_1_id: refA.id,
          dataset_1_version: refA.version,
          dataset_2_id: refB.id,
          dataset_2_version: refB.version,
          // Cross-system-model is blocked upstream, so both refs share this.
          system_model: refA.systemModel,
          method_id: selectedMethod.method_id,
        }
      : undefined;

  const { data: comparison, isLoading: loadingComparison, isError } = useComparison(comparisonParams);

  const { indicators, hasIdMismatch } = useMemo(
    () => buildComparedIndicators(comparison, [refA, refB]),
    [comparison, refA, refB]
  );

  const { dataset: datasetA } = useResolveCompareDataset(refA);
  const { dataset: datasetB } = useResolveCompareDataset(refB);

  // A release the user has no scores licence for answers both requests with a 403, which would
  // otherwise read as a failed request rather than "you can't see it".
  //
  // Only claim that once the access list has actually arrived. useAccess is disabled while
  // unauthenticated and returns undefined on error, and `checkAccessToResource(undefined, …)` is
  // false — so without the isSuccess gate a signed-out session, a still-loading request or a failed
  // one all render as "no licence", which is a confident and wrong answer.
  const { data: accessData, isSuccess: hasResolvedAccess, isLoading: loadingAccess } = useAccess();
  const unlicensedRefs = hasResolvedAccess
    ? refs.filter((ref) => !checkAccessToResource(accessData, AccessResourceKey.SCORES, ref.version))
    : [];

  // Also treat "asked but nothing back yet" as loading: the comparison query only becomes enabled
  // once a method resolves, and without this the render in between shows the empty state briefly.
  const isAwaitingComparison = Boolean(comparisonParams) && !comparison && !isError;
  // An unlicensed user first shows up as a 403 from the methods request, which can land before
  // /web/access does. Until access resolves, that failure can't be told apart from "no licence".
  // isLoading rather than !isSuccess: a signed-out user's access query is disabled, never succeeds,
  // and must not hold this view on skeletons forever.
  const isLoading = hasPair && (loadingMethods || loadingComparison || isAwaitingComparison || loadingAccess);
  // shouldRetry gives up on 4xx, leaving `data` undefined — identical to "no data" unless isError
  // is read. With one request for the whole tab, that distinction is the difference between
  // "nothing to show" and "we failed to ask". The same holds for the methods request: a failed one
  // also leaves `methods` undefined, which would otherwise read as an empty intersection.
  const hasFailed = hasPair && !isLoading && (isError || hasMethodsError || hasIdMismatch);
  // Genuinely empty, not merely unfetched — hence the !hasFailed guard. "These releases share no
  // method" is a claim about the data, so it must not stand in for a request that never succeeded.
  const hasNoMethods = hasPair && !isLoading && !hasFailed && (methods?.length ?? 0) === 0;
  const hasNoImpactData = hasPair && !isLoading && !hasNoMethods && !hasFailed && indicators.length === 0;
  const isCrossVersion = Boolean(refA && refB && refA.version !== refB.version);

  // Fires once per method + dataset combination: how often a valid selection yields nothing to show.
  const trackedNoDataKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasNoImpactData || !selectedMethod) return;
    const key = `${selectedMethod.method_name}|${refs.map((ref) => `${ref.id}:${ref.version}:${ref.systemModel}`).join(',')}`;
    if (trackedNoDataKeyRef.current === key) return;

    trackedNoDataKeyRef.current = key;
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_NO_IMPACT_DATA, {
      lcia_method: selectedMethod.method_name,
      dataset_ids: refs.map((ref) => ref.id),
      dataset_releases: refs.map((ref) => `${ref.version}-${ref.systemModel}`),
    });
  }, [hasNoImpactData, selectedMethod, refs]);

  const onMethodChange = (methodName: string) => {
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_METHOD_SELECTED, {
      lcia_method: methodName,
      previous_lcia_method: selectedMethod?.method_name,
      dataset_ids: refs.map((ref) => ref.id),
      dataset_releases: refs.map((ref) => `${ref.version}-${ref.systemModel}`),
    });
    setPickedMethodName(methodName);
  };

  return (
    <Stack spacing={5} pt={2}>
      <Heading size="md">Impact Assessment</Heading>

      <FormControl maxW={{ base: '100%', md: '360px' }}>
        <FormLabel fontSize="sm">LCIA Method</FormLabel>
        <Skeleton isLoaded={!loadingMethods}>
          <Select
            isDisabled={!methods?.length}
            value={selectedMethod?.method_name ?? ''}
            onChange={(event) => onMethodChange(event.target.value)}
          >
            {methods?.map((method) => (
              <option key={method.method_id} value={method.method_name}>
                {method.method_name}
              </option>
            ))}
          </Select>
        </Skeleton>
      </FormControl>

      {isCrossVersion && <CrossVersionCaveat refs={[refA, refB]} />}

      {!hasPair ? (
        <CrossReleaseNotice title="Select a second dataset">
          Impact scores are computed for a pair, so this tab needs two datasets. Go back to search and add another one to the
          comparison.
        </CrossReleaseNotice>
      ) : isLoading ? (
        <Stack spacing={4}>
          <Skeleton height="90px" />
          <Skeleton height="90px" />
          <Skeleton height="90px" />
        </Stack>
      ) : unlicensedRefs.length > 0 ? (
        <CrossReleaseNotice title="No licence for these impact scores">
          Your licence doesn&apos;t cover impact scores for {describeReleases(unlicensedRefs)}. The datasets can still be
          compared once that release is included in your licence.
        </CrossReleaseNotice>
      ) : hasNoMethods ? (
        <CrossReleaseNotice title="No LCIA method is available in both releases">
          These releases don&apos;t share an impact assessment method, so there is nothing to compare. Pick two datasets from
          releases that share a method.
        </CrossReleaseNotice>
      ) : hasFailed ? (
        <CrossReleaseNotice title="Couldn't load this comparison">
          {/* A method-list failure sends no score request and leaves the selector empty, so neither
              "the scores failed" nor "pick another method" would be true of it. */}
          {hasMethodsError
            ? "The LCIA methods available for these releases couldn't be retrieved. Reload the page to try again."
            : "The impact scores for these datasets couldn't be retrieved. Reload the page to try again, or pick a different LCIA method."}
        </CrossReleaseNotice>
      ) : hasNoImpactData ? (
        <Stack align="center" justify="center" spacing={2} bg={emptyBg} borderRadius="md" py={20} px={4} textAlign="center">
          <Icon as={HiOutlineDatabase} boxSize={12} color={emptyIconColor} />
          <Text fontWeight="semibold" color={grayText}>
            No impact data available for this selection
          </Text>
          <Text fontSize="sm" color={grayText}>
            This combination doesn&apos;t currently have data to display.
          </Text>
        </Stack>
      ) : (
        <Stack spacing={0}>
          <Grid
            templateColumns={IMPACT_GRID_COLUMNS}
            columnGap={6}
            pb={2}
            borderBottomWidth="1px"
            borderColor={borderColor}
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="wider"
            textTransform="uppercase"
            color={headerColor}
          >
            <GridItem>Impact Category / Standard Label</GridItem>
            <GridItem>Impact Score</GridItem>
            <GridItem>Unit</GridItem>
            <GridItem>Change</GridItem>
          </Grid>

          {indicators.map((indicator) => (
            <ImpactCategoryRow
              key={indicator.key}
              indicator={indicator}
              datasetIds={refs.map((ref) => ref.id)}
              lciaMethod={selectedMethod?.method_name}
            />
          ))}

          <HStack justify="space-between" align="flex-start" flexWrap="wrap" spacing={6} pt={6}>
            <Stack fontSize="sm" spacing={0}>
              <Text>
                <Text as="span" fontWeight="bold">
                  ds1
                </Text>{' '}
                = {datasetA?.activityName ?? '—'}
                {refA && (
                  <Text as="span" color={grayText}>
                    {' '}
                    ({refA.version}-{refA.systemModel})
                  </Text>
                )}
              </Text>
              <Text>
                <Text as="span" fontWeight="bold">
                  ds2
                </Text>{' '}
                = {datasetB?.activityName ?? '—'}
                {refB && (
                  <Text as="span" color={grayText}>
                    {' '}
                    ({refB.version}-{refB.systemModel})
                  </Text>
                )}
              </Text>
            </Stack>
            <Stack spacing={1} minW="240px">
              <Text fontSize="sm" fontWeight="semibold">
                Contributors to the impact category
              </Text>
              {/* Discrete bars, not a gradient: the ramp has a fixed number of steps and each one
                  maps to an actual segment, so showing a continuum overstated the granularity. */}
              <HStack spacing={1}>
                {contributorColors.map((color) => (
                  <Box key={color} flex={1} h="10px" borderRadius="sm" bg={color} />
                ))}
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="xs" color={grayText}>
                  highest
                </Text>
                <Text fontSize="xs" color={grayText}>
                  lowest
                </Text>
              </HStack>
              {/* Top-aligned, not centred, so the swatch sits on the label's first line. */}
              <HStack spacing={2} pt={1} align="flex-start">
                <Box
                  w="28px"
                  h="10px"
                  mt="3px"
                  borderRadius="sm"
                  flexShrink={0}
                  bg={noBreakdownBg}
                  backgroundImage={hatchBackgroundImage(noBreakdownHatch)}
                />
                <Text fontSize="xs" color={grayText}>
                  no contributor breakdown
                </Text>
              </HStack>
            </Stack>
          </HStack>
        </Stack>
      )}
    </Stack>
  );
};

export default ImpactAssessmentComparison;
